'use client';

import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { DEFAULT_VALUES } from '@/components/Filters/Filters.constants';
import { RequiredHeaderContextProps } from '@/types';

interface HomeWrapperContextProps extends RequiredHeaderContextProps {
  toggleVisibleHeader: (isVisible: boolean) => void;
}

type HomeProviderProps = {
  children: ReactNode;
};

const defaultContext: HomeWrapperContextProps = {
  query: '',
  formMethods: DEFAULT_VALUES as never,
  onChangeQuery: () => {},
  visible: false,
  toggleVisibleHeader: () => {}
};

export const HomeWrapperContext = createContext<
  HomeWrapperContextProps | undefined
>(undefined);

export const useHomeContext = () =>
  useContext(HomeWrapperContext) ?? defaultContext;

const HomeProviderWrapper = ({ children }: HomeProviderProps) => {
  const formMethods = useForm({
    defaultValues: DEFAULT_VALUES
  });

  const [query, setQuery] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(false);

  const toggleVisibleHeader = (isVisible: boolean) => setVisible(isVisible);

  const onChangeQuery = (queryValue: string) => {
    setQuery(queryValue);
    formMethods.setValue('searchTerm', queryValue || '');
  };

  const providerValue = useMemo(
    () => ({ query, onChangeQuery, formMethods, toggleVisibleHeader, visible }),
    [query, onChangeQuery, formMethods, toggleVisibleHeader, visible]
  );
  return (
    <HomeWrapperContext.Provider value={providerValue}>
      {children}
    </HomeWrapperContext.Provider>
  );
};

export const HomeProvider = (props: HomeProviderProps) => {
  const context = useContext(HomeWrapperContext);
  if (context) return <>{props.children}</>;

  return <HomeProviderWrapper {...props} />;
};
